import config from "../config";

export type AttachmentViewable = {
    url?: string;
    filename?: string;
    storagePath?: string;
    storageFullPath?: string;
    mimeType?: string;
    id?: string;
};

/** Chat message attachment via API (`attachmentIndex` is 0-based). `memberId` is the member whose chat is open. */
export type MessageAttachmentDownloadContext = {
    messageId: string;
    attachmentIndex: number;
    memberId?: string;
};

function buildMessageAttachmentQuery(
    context: MessageAttachmentDownloadContext,
): URLSearchParams {
    const params = new URLSearchParams();
    params.set("attachmentIndex", String(context.attachmentIndex));
    return params;
}

function messageAttachmentPath(memberId: string, messageId: string): string {
    return `/lmt/members/${encodeURIComponent(memberId)}/conversations/message/${encodeURIComponent(messageId)}/attachment`;
}

function openDirectUrl(attachment: AttachmentViewable): void {
    if (attachment.url) {
        window.open(attachment.url, "_blank");
    }
}

async function getIdToken(): Promise<string | undefined> {
    const { auth } = await import("../Firebase");
    return auth.currentUser?.getIdToken();
}

/**
 * Opens an attachment in a new tab. With `context`, uses the LMT message-attachment API
 * (`attachmentIndex` only; filename and MIME type come from Firestore on the server);
 * otherwise opens `url` (bookings, drafts, etc.).
 */
export async function viewAttachment(
    attachment: AttachmentViewable,
    context?: MessageAttachmentDownloadContext,
): Promise<void> {
    if (context?.memberId) {
        try {
            const token = await getIdToken();
            if (!token) {
                openDirectUrl(attachment);
                return;
            }
            const path = messageAttachmentPath(context.memberId, context.messageId);
            const params = buildMessageAttachmentQuery(context);
            const qs = params.toString();
            const url = `${config.apiServerBaseUrl}${path}${qs ? `?${qs}` : ""}`;
            const response = await fetch(url, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!response.ok) {
                throw new Error(`Failed to fetch: ${response.status}`);
            }
            const blob = await response.blob();
            const blobUrl = URL.createObjectURL(blob);
            window.open(blobUrl, "_blank");
            return;
        } catch {
            openDirectUrl(attachment);
            return;
        }
    }

    openDirectUrl(attachment);
}
